import React, { useState, useEffect } from 'react';
import {
  Box,
  Heading,
  Text,
  Button,
  Input,
  Textarea,
  VStack,
  HStack,
  Card,
  CardHeader,
  CardBody,
  FormControl,
  FormLabel,
  SimpleGrid,
  Badge,
  Spinner,
  useToast,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  IconButton,
} from '@chakra-ui/react';
import { AddIcon, EditIcon } from '@chakra-ui/icons';
import { supabase } from '../../supabaseClient';

// Admin CRUD for the managed drop-off location list. Phlebs pick one of the
// active rows here on each submission (see the Drop-off / Pickup section of the
// submission detail page). Deactivating keeps a location for history but hides
// it from the picker.
function DropOffLocations() {
  const toast = useToast();
  const [locations, setLocations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const blankForm = { name: '', address: '', notes: '' };
  const [form, setForm] = useState(blankForm);
  const [editingId, setEditingId] = useState(null);

  const adminUser = JSON.parse(localStorage.getItem('adminUser') || '{}');

  const fetchLocations = async () => {
    setLoading(true);
    const { data, error } = await supabase
      .from('drop_off_locations')
      .select('id, name, address, notes, active, created_at')
      .order('active', { ascending: false })
      .order('name', { ascending: true });
    if (error) {
      toast({ title: 'Error loading locations', description: error.message, status: 'error', duration: 5000, isClosable: true });
    } else {
      setLocations(data || []);
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchLocations();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const resetForm = () => {
    setForm(blankForm);
    setEditingId(null);
  };

  const handleSave = async () => {
    const name = form.name.trim();
    if (!name) {
      toast({ title: 'Name is required', status: 'warning', duration: 2500, isClosable: true });
      return;
    }
    setSaving(true);
    try {
      if (editingId) {
        const { error } = await supabase
          .from('drop_off_locations')
          .update({ name, address: form.address.trim() || null, notes: form.notes.trim() || null })
          .eq('id', editingId);
        if (error) throw error;
        toast({ title: 'Location updated', status: 'success', duration: 2500, isClosable: true });
      } else {
        const { error } = await supabase
          .from('drop_off_locations')
          .insert({ name, address: form.address.trim() || null, notes: form.notes.trim() || null, created_by: adminUser?.id || null });
        if (error) throw error;
        toast({ title: 'Location added', status: 'success', duration: 2500, isClosable: true });
      }
      resetForm();
      fetchLocations();
    } catch (err) {
      toast({ title: 'Save failed', description: err.message, status: 'error', duration: 5000, isClosable: true });
    } finally {
      setSaving(false);
    }
  };

  const handleEdit = (loc) => {
    setEditingId(loc.id);
    setForm({ name: loc.name || '', address: loc.address || '', notes: loc.notes || '' });
  };

  const toggleActive = async (loc) => {
    const { error } = await supabase
      .from('drop_off_locations')
      .update({ active: !loc.active })
      .eq('id', loc.id);
    if (error) {
      toast({ title: 'Update failed', description: error.message, status: 'error', duration: 5000, isClosable: true });
      return;
    }
    fetchLocations();
  };

  return (
    <Box>
      <Heading size="lg" color="gray.700" mb={1}>Drop-off Locations</Heading>
      <Text fontSize="sm" color="gray.500" mb={5}>
        Approved sites where phlebotomists can drop off samples. Active locations appear in the picker on each submission.
      </Text>

      <Card mb={6}>
        <CardHeader bg="purple.50">
          <Heading size="md" color="purple.700">{editingId ? 'Edit location' : 'Add a location'}</Heading>
        </CardHeader>
        <CardBody>
          <VStack align="stretch" spacing={4}>
            <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
              <FormControl isRequired>
                <FormLabel>Name</FormLabel>
                <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="e.g. Quest — Union, NJ" />
              </FormControl>
              <FormControl>
                <FormLabel>Address</FormLabel>
                <Input value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} placeholder="Street, City, State ZIP" />
              </FormControl>
            </SimpleGrid>
            <FormControl>
              <FormLabel>Notes</FormLabel>
              <Textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} rows={2} placeholder="Hours, contact, instructions…" />
            </FormControl>
            <HStack>
              <Button colorScheme="purple" leftIcon={editingId ? <EditIcon /> : <AddIcon />} onClick={handleSave} isLoading={saving}>
                {editingId ? 'Save changes' : 'Add location'}
              </Button>
              {editingId && <Button variant="ghost" onClick={resetForm}>Cancel</Button>}
            </HStack>
          </VStack>
        </CardBody>
      </Card>

      <Card>
        <CardBody>
          {loading ? (
            <HStack py={6} justify="center"><Spinner /><Text color="gray.500">Loading…</Text></HStack>
          ) : locations.length === 0 ? (
            <Text color="gray.500" py={4}>No drop-off locations yet. Add your first one above.</Text>
          ) : (
            <Box overflowX="auto">
              <Table size="sm">
                <Thead>
                  <Tr>
                    <Th>Name</Th>
                    <Th>Address</Th>
                    <Th>Notes</Th>
                    <Th>Status</Th>
                    <Th>Actions</Th>
                  </Tr>
                </Thead>
                <Tbody>
                  {locations.map((loc) => (
                    <Tr key={loc.id} opacity={loc.active ? 1 : 0.55}>
                      <Td fontWeight="medium">{loc.name}</Td>
                      <Td>{loc.address || '—'}</Td>
                      <Td>{loc.notes || '—'}</Td>
                      <Td>
                        <Badge colorScheme={loc.active ? 'green' : 'gray'}>{loc.active ? 'Active' : 'Inactive'}</Badge>
                      </Td>
                      <Td>
                        <HStack spacing={1}>
                          <IconButton aria-label="Edit" icon={<EditIcon />} size="xs" variant="ghost" onClick={() => handleEdit(loc)} />
                          <Button size="xs" variant="outline" colorScheme={loc.active ? 'red' : 'green'} onClick={() => toggleActive(loc)}>
                            {loc.active ? 'Deactivate' : 'Reactivate'}
                          </Button>
                        </HStack>
                      </Td>
                    </Tr>
                  ))}
                </Tbody>
              </Table>
            </Box>
          )}
        </CardBody>
      </Card>
    </Box>
  );
}

export default DropOffLocations;
